import getConfig from 'next/config';
import { encryptBinary } from 'lib/e2ee/helper';
import { packsClient, userClient } from 'lib/clients';

const { publicRuntimeConfig: { genericRESTUrl } } = getConfig();

const sliceSize = 1024 * 1024 * 5; // 5MB
const maxFileSize = 1073741824; // 1GB

export enum AvatarTypes {
  USER = "user",
}

export class PackFileUploader {
    packId: string;

    constructor(packId: string) {
        this.packId = packId;
    }

    upload(attachment: any, onUploadProgress?: (progressEvent: any) => void) {
        return packsClient.upload(this.packId, attachment, onUploadProgress);
    }

    startChunk(attachment: any, onUploadProgress?: (progressEvent: any) => void) {
        return packsClient.startChunk(this.packId, attachment, onUploadProgress);
    }

    chunk(handle: string, attachment: any, number: number, onUploadProgress?: (progressEvent: any) => void) {
        return packsClient.chunk(this.packId, handle, attachment, number, onUploadProgress);
    }

    endChunk(handle: string, attachment: any, number: number, onUploadProgress?: (progressEvent: any) => void) {
        return packsClient.endChunk(this.packId, handle, attachment, number, onUploadProgress);
    }
}

export class UserFileUploader {
    upload(attachment: any, onUploadProgress?: (progressEvent: any) => void) {
        return userClient.upload(attachment, onUploadProgress);
    }

    startChunk(attachment: any, onUploadProgress?: (progressEvent: any) => void) {
        return userClient.startChunk(attachment, onUploadProgress);
    }

    chunk(handle: string, attachment: any, number: number, onUploadProgress?: (progressEvent: any) => void) {
        return userClient.chunk(handle, attachment, number, onUploadProgress);
    }

    endChunk(handle: string, attachment: any, number: number, onUploadProgress?: (progressEvent: any) => void) {
        return userClient.endChunk(handle, attachment, number, onUploadProgress);
    }
}

export const uploadAvatar = (type: AvatarTypes.USER, avatar: any, callback?: Function) => {
  return new Promise(function (resolve, reject) {
    let url = `${genericRESTUrl}/v1/`;

    if (type === AvatarTypes.USER) {
      url += 'user/avatar'
    }

    const xhr = new XMLHttpRequest();
    const formData = new FormData();

    if (xhr.upload && callback) {
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          callback(percentComplete)
        }
      }
    }

    xhr.open('POST', url, true);
    xhr.withCredentials = true;

    xhr.addEventListener('readystatechange', () => {
      if (xhr.readyState === 4 && (xhr.status === 200 || xhr.status === 204)) {
        resolve('');
      }
      else if (xhr.readyState === 4 && xhr.status !== 200 && xhr.status !== 204) {
        // Error. Inform the user
        reject({ status: xhr.status });
      }
    })

    formData.append('avatar', avatar);
    xhr.send(formData);
  });
}


class UploadError extends Error {
  response: {
    status: number;
  };
  constructor(status: number) {
    super(`Upload error response ${status}`);
    this.response = { status };
  }
}

const sleep = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
}

class Pool {
  size: number;
  count: number;
  queue: any[];
  resolve: any;
  reject: any;
  failure: any;
  uploader: any;

  constructor(uploader: any, size = 4) {
    this.size = size;
    this.uploader = uploader;
    this.count = 0;
    this.queue = [];
    this.resolve = null;
    this.reject = null;
    this.failure = null;
  }

  send(chunkNum: number) {
    if (this.failure) {
      throw new UploadError(this.failure.status);
    }

    if (this.size === this.count) {
      this.queue.push(chunkNum)
    }
    else {
      this.runSend(chunkNum);
    }
  }

  async runSend(chunkNum: number) {
    this.count++;

    try {
      this.uploader.sendWithRetry('chunk', chunkNum).then(() => {
        this.cleanUp();
      }).catch((err: any) => {
        this.failure = err;
        this.queue = [];
        this.count = 0;

        if (this.reject) {
          this.reject(err);
        }
      });
    }
    catch (err) {
      console.log(err);
      this.failure = err;
      this.queue = [];
      this.count = 0;

      if (this.reject) {
        this.reject(err);
      }
    }
  }

  cleanUp() {
    this.count--;
    if (this.queue.length > 0) {
      const chunkNum = this.queue.shift();
      this.runSend(chunkNum);
    }
    else if (this.count === 0 && this.resolve) {
      this.resolve();
    }
  }

  waitForQueue() {
    return new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;

      if (this.failure) {
        this.queue = [];
        this.count = 0;

        reject(this.failure);
      }
    });
  }
}

class ChunkedUploader {
  isEncrypted: boolean;
  file: any;
  lastBlock: number;
  totalChunks: number;
  percentTracking: number[];
  handle?: string;
  onProgress: (percent: number) => void;
  sessionKey?: any;
  client: any;

  constructor(client: any, isEncrypted: boolean, file: any, onProgress = (_: number) => { }, sessionKey?: any) {
    this.client = client;

    this.isEncrypted = isEncrypted;
    this.file = file
    this.handle = null;
    this.onProgress = onProgress;
    this.sessionKey = sessionKey

    this.totalChunks = Math.ceil(this.file.size / sliceSize);
    this.lastBlock = this.totalChunks - 1;

    this.percentTracking = [];

    for (let x = 0; x < this.lastBlock; x++) {
      this.percentTracking.push(0);
    }
  }

  calcTotalPercent() {
    let total = 0;

    for (const percent of this.percentTracking) {
      total += percent;
    }

    this.onProgress(Math.floor(total / this.totalChunks));
  }

  fileChunk(block: number) {
    const start = block * sliceSize
    const end = start + sliceSize;

    const blob = end > this.file.size ? this.file.slice(start) : this.file.slice(start, end);

    return new File([blob], this.file.name, { type: this.file.type });
  }

  uploadChunk = async (type: string, chunkNum: number, chunk: any, callback: (progressEvent: any) => void) => {
    if (type === 'start_chunk') {
      return await this.client.startChunk(chunk, callback);
    }

    if (type === 'end_chunk') {
      return await this.client.endChunk(this.handle, chunk, chunkNum, callback);
    }

    return await this.client.chunk(this.handle, chunk, chunkNum, callback);
  }

  async sendWithRetry(type: string, chunkNum: number, callback?: Function) {
    const chunk = this.fileChunk(chunkNum);

    do {
      try {
        const response = await this.uploadChunk(type, chunkNum, chunk, (progressEvent: any) => {
          if (progressEvent.lengthComputable) {
            const percentComplete = (progressEvent.loaded / progressEvent.total) * 100;

            this.percentTracking[chunkNum] = percentComplete;
            this.calcTotalPercent();
          }
        });

        if (callback) callback();

        return response;
      }
      catch (err) {
        console.log(err);
        const status = err?.response?.status;

        if (status < 500 || status === 507) {
          throw err;
        }
      }

      await sleep(5000);
    }
    while (true);
  }

  async uploadStartChunk() {
    const response = await this.sendWithRetry('start_chunk', 0);

    if (response.status === 200 && response.data.handle) {
      this.handle = response.data.handle;
    }
    else {
      throw new UploadError(500); // Something seriously wrong happened.
    }
  }

  async uploadChunks() {
    const pool = new Pool(this);

    for (let x = 1; x < this.lastBlock; x++) {
      pool.send(x);
    }

    await pool.waitForQueue();
  }

  async uploadEndChunk() {
    return await this.sendWithRetry('end_chunk', this.lastBlock);
  }

  async upload() {
    await this.uploadStartChunk();

    if (this.lastBlock > 1) { // If only block zero and one, then skip upload chunks.
      await this.uploadChunks();
    }

    const { data } = await this.uploadEndChunk();

    return data.token;
  }
}

export class Uploader {
  isEncrypted: boolean;
  files: any;
  onProgress: (percent: number) => void;
  totalSize: number;
  filePercents: number[];
  percentTracking: number[];
  sessionKey?: any;
  client: any;

  constructor(client: any, isEncrypted: boolean, files: any, onProgress = (_: number) => { }, sessionKey?: any) {
    this.client = client;

    this.isEncrypted = isEncrypted;
    this.onProgress = onProgress;
    this.sessionKey = sessionKey;

    this.files = [...files];
    this.totalSize = 0;

    this.percentTracking = [];
    this.filePercents = [];

    for (let x = 0; x < this.files.length; x++) {
      const file = this.files[x];

      this.totalSize += file.size;
      this.percentTracking.push(0);
    }

    for (let x = 0; x < this.files.length; x++) {
      const file = this.files[x];
      this.filePercents.push(file.size / this.totalSize);
    }
  }

  calcTotalPercent() {
    let totalPercent = 0;

    for (let x = 0; x < this.percentTracking.length; x++) {
      totalPercent += this.percentTracking[x] * this.filePercents[x];
    }

    this.onProgress(totalPercent);
  }

  async uploadFull(attachment: any, fileNum: number) {
    const { data } = await this.client.upload(attachment, (progressEvent: any) => {
      if (progressEvent.lengthComputable) {
        const percentComplete = (progressEvent.loaded / progressEvent.total) * 100;

        this.percentTracking[fileNum] = percentComplete;
        this.calcTotalPercent();
      }
    });

    return data.token;
  }

  async uploadFileAsChunks(file: any, fileNum: number) {
    const chunkUploader = new ChunkedUploader(this.client, this.isEncrypted, file, (percent: number) => {
      this.percentTracking[fileNum] = percent;
      this.calcTotalPercent();
    }, this.sessionKey);

    return await chunkUploader.upload();
  }

  fileToArray(file: any) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function () {
        const arrayBuffer = this.result as any;
        const array = new Uint8Array(arrayBuffer);

        resolve(array);
      }
      reader.readAsArrayBuffer(file);
    });
  }

  async upload() {
    const response = {
      files: [],
      upgradeRequired: false,
      exceededStorage: false,
      successCount: 0,
      failureCount: 0,
      tooLargeCount: 0
    }

    for (let x = 0; x < this.files.length; x++) {
      const size = this.files[x].size;
      const type = this.files[x].type;
      const name = this.files[x].name || 'Untitled';

      let bytes = await this.fileToArray(this.files[x]) as any;

      if (this.isEncrypted) {
        bytes = await encryptBinary(bytes, this.sessionKey) as any;
      }

      const blob = new Blob([bytes], { type });
      const file = new File([blob], name, { type });

      try {
        if (size > maxFileSize) {
          throw new UploadError(413);
        }

        if (size > sliceSize) {
          const token = await this.uploadFileAsChunks(file, x);
          response.files.push({ token, data: file });
        }
        else {
          const token = await this.uploadFull(file, x);
          response.files.push({ token, data: file });
        }
        response.successCount++;
      }
      catch (e) {
        console.log(e);
        if (e?.response) {
          if (e.response.status === 507) {
            response.exceededStorage = true;
            return response;
          }
          else if (e.response.status === 402) {
            response.upgradeRequired = true;
            return response;
          }
          else if (e.response.status === 413) {
            response.tooLargeCount++
          }
          else {
            response.failureCount++;
          }
        }
        else {
          response.failureCount++;
        }
      }
    }

    return response;
  }
}
